import {
  useGetLayoutOptimizationsQuery,
  useGetPackagesByTypeQuery,
  useGetStructuralEngineeringsQuery,
  useProjectId,
  useServiceName,
} from '@consigli/hooks';
import { DocumentPackageType, ServiceName } from '@consigli/types';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormCheckbox } from '@/components/form-checkbox';
import { ServiceIdIndexValue } from '@/routes';

import { IterationName } from './iteration-name';
import { SelectLanguage } from './select-language';
import { WizardNextButton } from './wizard-next-button';
import { useValidateName } from '../hooks/use-validate-name';

export const useFetchValidateName = (
  getPackagesQuery:
    | typeof useGetPackagesByTypeQuery
    | typeof useGetStructuralEngineeringsQuery
    | typeof useGetLayoutOptimizationsQuery,
  { projectId, type }: { projectId: number; type?: DocumentPackageType },
) => {
  const validateName = useValidateName(getPackagesQuery, {
    projectId,
    type,
  });
  return { validateName };
};

export const InsightForm = () => {
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const projectId = useProjectId();
  const { formState, getValues } = useFormContext<FieldValues>();
  const { isDirty } = formState;
  const formData = getValues();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });
  return (
    <>
      <IterationName
        validateName={validateName}
        label={
          serviceName === ServiceName.OFFER
            ? t('package-creation.provider')
            : t('package-creation.analysis-name')
        }
        errorMessage={`${t('createproject.invalid-project-name-empty')}*`}
      />
      <SelectLanguage />
      <WizardNextButton disabled={!isDirty && !formData.iterationName && !formData.language} />
    </>
  );
};
export const FdvForm = () => {
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const projectId = useProjectId();
  const type = ServiceIdIndexValue[serviceName] as DocumentPackageType;
  const { formState, getValues } = useFormContext<FieldValues>();
  const formData = getValues();
  const { isDirty } = formState;
  const { validateName } = useFetchValidateName(useGetPackagesByTypeQuery, {
    projectId,
    type,
  });
  return (
    <>
      <IterationName
        validateName={validateName}
        label={
          serviceName === ServiceName.OFFER
            ? t('package-creation.provider')
            : t('package-creation.analysis-name')
        }
        errorMessage={`${t('createproject.invalid-project-name-empty')}*`}
      />
      <SelectLanguage />
      <FormCheckbox name="needClassification" labelKey="package-creation.need-classification" />
      <WizardNextButton disabled={!isDirty && !formData.iterationName && !formData.language} />
    </>
  );
};
export const StructuralForm = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const { formState, getValues } = useFormContext<FieldValues>();
  const { isDirty } = formState;
  const formData = getValues();
  const { validateName } = useFetchValidateName(useGetStructuralEngineeringsQuery, {
    projectId,
  });
  return (
    <>
      <IterationName
        validateName={validateName}
        label={t('package-creation.building-name')}
        errorMessage={`${t('package-creation.building-name-required')}*`}
      />
      <WizardNextButton disabled={!isDirty && !formData.iterationName} />
    </>
  );
};
